// frontend/src/services/api.js
//
// Stable org+token handling that survives URL cleaning:
// - orgId is read from URL, then localStorage, then decoded from the embed token.
// - Once discovered, orgId is persisted in localStorage so subsequent requests
//   remain tenant-scoped even after the URL is cleaned.
// - JOBS endpoints REQUIRE orgId and will throw if missing.
// - Jobs fetching prefers PUBLIC endpoints first (to avoid 404 spam),
//   then falls back to private careers endpoints if needed.
// - Candidate login/me endpoints are tried across singular/plural and with/without
//   the /careers prefix for maximum backend compatibility.
// - Adds forgot-password + reset-password helpers.
// - getCandidateAccount() now resolves via candMe() (we no longer call /candidate/account).
//
// All previous exports are preserved.

//////////////////////////////
// Base URL normalization
//////////////////////////////
function normalizeApiRoot(raw) {
  if (!raw) return "/api"; // sane default behind reverse proxy
  const t = String(raw).trim().replace(/\/+$/, "");

  if (t.startsWith("/")) {
    if (t === "/api" || t.startsWith("/api/")) return t;
    return t + "/api";
  }

  try {
    const u = new URL(t);
    const pathname = u.pathname.replace(/\/+$/, "");
    const hasApi = /^\/api(\/|$)/.test(pathname);
    const newPath = hasApi ? pathname : (pathname || "") + "/api";
    return u.origin + newPath;
  } catch {
    return "/api";
  }
}

const RAW_API_ROOT =
  process.env.REACT_APP_CAREERS_API_ROOT ||
  process.env.REACT_APP_CAREERS_API_BASE ||
  "/api";

export const API_ROOT = normalizeApiRoot(RAW_API_ROOT);

// Private (careers) and public bases
export const CareersAPIBase = `${API_ROOT.replace(/\/+$/, "")}/careers`;
const PublicAPIBase = `${API_ROOT.replace(/\/+$/, "")}/public`;

//////////////////////////////
// Local storage/token helpers
//////////////////////////////
const CAND_TOKEN_KEY = "careers_candidate_token";
const EMBED_TOKEN_SKEY = "careers_embed_token";
const ORG_SKEY = "selectedOrgId";
let __EMBED_TOKEN_MEM__ = "";

//////////////////////////////
// JWT helpers (no verify)
//////////////////////////////
function base64UrlDecode(str) {
  try {
    const s = str.replace(/-/g, "+").replace(/_/g, "/");
    const pad = s.length % 4 === 2 ? "==" : s.length % 4 === 3 ? "=" : "";
    return atob(s + pad);
  } catch {
    return "";
  }
}

function decodeJwtNoVerify(jwt) {
  if (!jwt || typeof jwt !== "string") return null;
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]) || "{}");
    return payload || null;
  } catch {
    return null;
  }
}

//////////////////////////////
// Embed token discovery
//////////////////////////////
export function getTokenFromSearch() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const t = params.get("token");
    if (t && t !== "null" && t !== "NULL") return t;
  } catch {}
  return null;
}

export function getEmbedTokenFromHash() {
  try {
    const hash = window.location.hash || "";
    // supports "#token=xxx" at any position
    const m = hash.match(/(?:^|[?#&])token=([^&]+)/);
    if (m) {
      const t = decodeURIComponent(m[1]);
      if (t && t !== "null" && t !== "NULL") {
        persistEmbedToken(t);
        return t;
      }
    }
  } catch {}
  const persisted = getPersistedEmbedToken();
  return persisted || null;
}

export function persistEmbedToken(tok) {
  try {
    if (tok && tok !== "null" && tok !== "NULL") {
      __EMBED_TOKEN_MEM__ = tok;
      sessionStorage.setItem(EMBED_TOKEN_SKEY, tok);
    }
  } catch {}
}

function getPersistedEmbedToken() {
  try {
    if (__EMBED_TOKEN_MEM__) return __EMBED_TOKEN_MEM__;
    const ss = sessionStorage.getItem(EMBED_TOKEN_SKEY);
    return ss || "";
  } catch {
    return "";
  }
}

function getEffectiveEmbedToken() {
  return (
    getEmbedTokenFromHash() ||
    getTokenFromSearch() ||
    getPersistedEmbedToken() ||
    ""
  );
}

//////////////////////////////
// OrgId resolution (sticky)
//////////////////////////////
function getOrgIdFromSearchOnly() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const v = params.get("orgId");
    return v != null ? v : null;
  } catch {
    return null;
  }
}

// Backward-compat shim for existing imports in the app:
export function getOrgIdFromSearch() {
  return getOrgIdFromSearchOnly();
}

function persistOrgId(orgId) {
  try {
    if (orgId != null) localStorage.setItem(ORG_SKEY, String(orgId));
  } catch {}
}

function getOrgIdFromStore() {
  try {
    const v = localStorage.getItem(ORG_SKEY);
    return v || null;
  } catch {
    return null;
  }
}

function getOrgIdFromToken(tok) {
  const payload = decodeJwtNoVerify(tok);
  if (payload && (payload.orgId != null || payload.orgID != null)) {
    return String(payload.orgId ?? payload.orgID);
  }
  return null;
}

// Non-throwing (convenience)
export function getOrgId() {
  const fromSearch = getOrgIdFromSearchOnly();
  if (fromSearch) {
    persistOrgId(fromSearch);
    return fromSearch;
  }

  const fromStore = getOrgIdFromStore();
  if (fromStore) return fromStore;

  const tok = getEffectiveEmbedToken();
  const fromToken = getOrgIdFromToken(tok);
  if (fromToken) {
    persistOrgId(fromToken);
    return fromToken;
  }

  return null;
}

// Throwing (use for tenant-scoped endpoints)
function getOrgIdOrThrow(explicitOrgId) {
  const o = explicitOrgId ?? getOrgId();
  if (o == null || o === "" || o === "null" || o === "NULL") {
    throw new Error("orgId is required");
  }
  return String(o);
}

//////////////////////////////
// Handshake compatibility
//////////////////////////////
export async function exchangeEST() {
  try {
    let tok = getEmbedTokenFromHash();
    if (!tok || tok === "null" || tok === "NULL") {
      const qsTok = getTokenFromSearch();
      if (qsTok) tok = qsTok;
    }
    if (tok) persistEmbedToken(tok);

    const maybeOrg = getOrgIdFromToken(tok);
    if (maybeOrg) persistOrgId(maybeOrg);

    return !!tok;
  } catch {
    return false;
  }
}

export function bootstrapOrgCookie() {
  try {
    const o = getOrgIdFromSearchOnly();
    if (o) persistOrgId(o);

    if (!getOrgIdFromStore()) {
      const tok = getEffectiveEmbedToken();
      const o2 = getOrgIdFromToken(tok);
      if (o2) persistOrgId(o2);
    }
  } catch {}
}

//////////////////////////////
// Candidate token helpers
//////////////////////////////
export function getCandidateToken() {
  try {
    // keep backward compat for existing localStorage key
    return localStorage.getItem("candidate_jwt") || localStorage.getItem(CAND_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setCandidateToken(token) {
  try {
    if (token) {
      window.localStorage.setItem("candidate_jwt", token);
      window.localStorage.setItem(CAND_TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem("candidate_jwt");
      window.localStorage.removeItem(CAND_TOKEN_KEY);
    }
  } catch {}
}

//////////////////////////////
// Generic fetch wrapper
//////////////////////////////
export async function apiFetch(path, opts = {}) {
  let token = opts.token;
  if (token == null || token === "null" || token === "NULL") {
    token = getEffectiveEmbedToken();
    if (token) persistEmbedToken(token);
  }

  let fullPath = path;

  // Append token only for GETs (avoid noisy query params on POST/PUT)
  if ((opts.method === "GET" || !opts.method) && token && !fullPath.includes("token=")) {
    const sep = fullPath.includes("?") ? "&" : "?";
    fullPath += `${sep}token=${encodeURIComponent(token)}`;
  }

  try {
    const u = new URL(fullPath, window.location.origin);
    const existing = u.searchParams.get("token");
    if (!existing || existing === "null" || existing === "NULL") {
      if (token && token !== "null" && token !== "NULL") {
        u.searchParams.set("token", token);
      } else {
        u.searchParams.delete("token");
      }
    }
    fullPath = u.pathname + (u.search || "") + (u.hash || "");
  } catch {}

  // Join with API root (avoid /api/api)
  const root = API_ROOT.replace(/\/+$/, "");
  let url;
  if (/^https?:\/\//i.test(fullPath)) {
    url = fullPath;
  } else if (/^\/api(\/|$)/.test(fullPath)) {
    try {
      const r = new URL(root);
      url = r.origin + fullPath;
    } catch {
      url = `${root}${fullPath}`;
    }
  } else {
    url = `${root}${fullPath.startsWith("/") ? "" : "/"}${fullPath}`;
  }

  const headers = new Headers(opts.headers || {});
  headers.set("Accept", "application/json");

  if (
    !headers.has("Content-Type") &&
    opts.body &&
    typeof opts.body === "object" &&
    !(opts.body instanceof FormData)
  ) {
    headers.set("Content-Type", "application/json");
  }

  const candToken = getCandidateToken();
  if (candToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${candToken}`);
  }

  // Propagate org + embed token via headers if provided in opts
  if (opts.orgId != null && !headers.has("x-org-id")) {
    headers.set("x-org-id", String(opts.orgId));
  }
  if (token && token !== "null" && token !== "NULL" && !headers.has("x-embed-token")) {
    headers.set("x-embed-token", token);
  }

  const res = await fetch(url, {
    method: opts.method || "GET",
    headers,
    body:
      headers.get("Content-Type") === "application/json" &&
      opts.body &&
      typeof opts.body === "object" &&
      !(opts.body instanceof FormData)
        ? JSON.stringify(opts.body)
        : opts.body || undefined,
    credentials: "include",
    mode: "cors",
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text || "{}");
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const err = new Error((data && data.error) || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

//////////////////////////////
// Auto-token generation
//////////////////////////////

/**
 * Automatically generate an embed token by calling /api/embed/authorize
 * This allows the app to work with just ?orgId=18 without requiring manual token generation
 */
export async function autoGenerateToken(orgId) {
  if (!orgId) {
    console.warn('[autoGenerateToken] orgId is required');
    return null;
  }
  
  try {
    const response = await fetch(`${API_ROOT}/embed/authorize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgId: Number(orgId) }),
      credentials: 'include',
    });
    
    if (!response.ok) {
      console.error('[autoGenerateToken] Failed to generate token:', response.status);
      return null;
    }
    
    const data = await response.json();
    const token = data?.token;
    
    if (token) {
      console.log('[autoGenerateToken] Token generated successfully');
      persistEmbedToken(token);
      persistOrgId(orgId);
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('[autoGenerateToken] Error:', error);
    return null;
  }
}

//////////////////////////////
// Ping (careers)
//////////////////////////////

// Hardened ping: prefers embed ping if token is available; never throws on 401
export async function ping({ orgId } = {}) {
  try {
    // Try a stateless embed ping if we have an embed token and orgId
    const est =
      (typeof getEmbedTokenFromHash === "function" && getEmbedTokenFromHash()) || null;
    const stored =
      (typeof window !== "undefined" &&
        window.sessionStorage &&
        window.sessionStorage.getItem("careers_embed_token")) || null;
    const embedTok = est || stored || null;

    if (embedTok && orgId) {
      const u = new URL(`${API_ROOT}/embed/ping`, window.location.origin);
      u.searchParams.set("orgId", String(orgId));
      u.searchParams.set("token", embedTok);
      const r = await fetch(u.toString(), { credentials: "include" });
      if (r.ok) return r.json();
      // fallthrough
    }

    // Careers ping (cookie/session based)
    const u2 = new URL(`${API_ROOT}/careers/ping`, window.location.origin);
    if (orgId) u2.searchParams.set("orgId", String(orgId));
    const r2 = await fetch(u2.toString(), { credentials: "include" });
    if (r2.ok) return r2.json();

    return { ok: false, error: "unauthorized" }; // soft failure
  } catch {
    return { ok: false };
  }
}

//////////////////////////////
// JOBS (Public-first, org REQUIRED)
//////////////////////////////
export async function searchJobs({ orgId, token, q = "", page = 1, limit = 25 } = {}) {
  const finalOrg = getOrgIdOrThrow(orgId);

  // 1) Try PUBLIC first to avoid 404 spam
  const pubQs = new URLSearchParams();
  pubQs.set("orgId", String(finalOrg));
  if (q) pubQs.set("q", q);
  pubQs.set("page", String(page));
  pubQs.set("pageSize", String(limit));

  const publicUrl = `${PublicAPIBase}/jobs?${pubQs.toString()}`;
  const pubRes = await fetch(publicUrl, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });

  if (pubRes.ok) {
    return pubRes.json();
  }

  // 2) Fall back to private aliases
  const query = new URLSearchParams();
  if (q) query.set("q", q);
  query.set("page", String(page));
  query.set("limit", String(limit));
  const privateSearch = `${CareersAPIBase}/jobs/search?${query.toString()}`;

  try {
    return await apiFetch(privateSearch, { orgId: finalOrg, token });
  } catch (err) {
    if (err && err.status === 404) {
      const alias = `${CareersAPIBase}/jobs?${query.toString()}`;
      return apiFetch(alias, { orgId: finalOrg, token });
    }
    throw err;
  }
}

export async function getJob({ orgId, token, id }) {
  const finalOrg = getOrgIdOrThrow(orgId);

  // 1) Try PUBLIC first
  const qs = new URLSearchParams();
  qs.set("orgId", String(finalOrg));
  const publicUrl = `${PublicAPIBase}/job/${encodeURIComponent(id)}?${qs.toString()}`;
  const res = await fetch(publicUrl, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (res.ok) return res.json();

  // 2) Fall back to private
  const privatePath = `${CareersAPIBase}/jobs/${encodeURIComponent(id)}`;
  try {
    return await apiFetch(privatePath, { orgId: finalOrg, token });
  } catch (err) {
    if (err && err.status === 404) {
      let bodyText = "";
      try {
        bodyText = await res.text();
      } catch {}
      throw new Error(
        `Job not found for orgId=${finalOrg} (public/private). ${bodyText || ""}`.trim()
      );
    }
    throw err;
  }
}

//////////////////////////////
// Candidate auth/account
//////////////////////////////
export async function candRegister({ orgId, token, name, email, password }) {
  const finalOrg = orgId ?? getOrgId();
  return apiFetch(`${CareersAPIBase}/candidate/register`, {
    orgId: finalOrg,
    token,
    method: "POST",
    body: { name, email, password },
  });
}

// Minimal login: flat route handled by server.js middleware to /candidate/login
export async function candLogin({ email, password }) {
  const u = new URL(`${API_ROOT}/candidate/login`, window.location.origin);
  const r = await fetch(u, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`Login failed (${r.status})`);
  const data = await r.json();
  if (data?.token) {
    setCandidateToken(data.token);
  }
  return data;
}

// Simple /me: flat route handled by server.js middleware to /candidate/me
export async function candMe() {
  const token = getCandidateToken();
  if (!token) return null;
  const r = await fetch(new URL(`${API_ROOT}/candidate/me`, window.location.origin), {
    credentials: "include",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) return null;
  return r.json();
}

// Forgot password (try multiple routes)
export async function candForgotPassword({ orgId, token, email }) {
  const finalOrg = orgId ?? getOrgId();
  const body = { email };
  const targets = [
    `${CareersAPIBase}/candidate/forgot`,
    `${CareersAPIBase}/candidates/forgot`,
    `${API_ROOT.replace(/\/+$/, "")}/candidate/forgot`,
    `${API_ROOT.replace(/\/+$/, "")}/candidates/forgot`,
  ];
  let lastErr;
  for (const t of targets) {
    try {
      return await apiFetch(t, { method: "POST", orgId: finalOrg, token, body });
    } catch (e) {
      if (e.status === 404) {
        lastErr = e;
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("Forgot-password route not found");
}

// Password reset submit (code or token, depending on backend)
export async function candPasswordResetSubmit({ orgId, token, email, code, password }) {
  const finalOrg = orgId ?? getOrgId();
  const body = { email, code, password };
  const targets = [
    `${CareersAPIBase}/candidate/reset`,
    `${CareersAPIBase}/candidates/reset`,
    `${API_ROOT.replace(/\/+$/, "")}/candidate/reset`,
    `${API_ROOT.replace(/\/+$/, "")}/candidates/reset`,
  ];
  let lastErr;
  for (const t of targets) {
    try {
      return await apiFetch(t, { method: "POST", orgId: finalOrg, token, body });
    } catch (e) {
      if (e.status === 404) {
        lastErr = e;
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("Reset-password route not found");
}

// ✅ FIXED: getCandidateAccount now fetches full profile data from correct endpoint
export async function getCandidateAccount() {
  const token = getCandidateToken();
  if (!token) return null;
  
  // Fetch from /careers/candidate/account endpoint (mounted at /api/careers)
  try {
    const r = await fetch(new URL(`${API_ROOT}/careers/candidate/account`, window.location.origin), {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const data = await r.json();
      // The backend returns { ok: true, candidate: { id, name, email, ... } }
      // We need to map these to the expected candidate_* format
      const cand = data?.candidate || {};
      return {
        candidate: {
          candidate_id: cand.id,
          candidate_name: cand.name || "",
          candidate_email: cand.email || "",
          candidate_phone: cand.phone || "",
          candidate_linkedin_url: cand.linkedin_url || "",
          candidate_avatar: cand.avatar_url || "",
          candidate_avatar_url: cand.avatar_url || "",
          avatar_url: cand.avatar_url || "",
          avatar_full_url: cand.avatar_url || "",
          verified: cand.verified || false,
        }
      };
    }
  } catch (e) {
    console.warn("Account endpoint failed:", e);
  }
  
  // Fallback: use candMe() and map the fields
  const meData = await candMe();
  if (!meData) return null;
  
  const candidate = meData.candidate || meData;
  return {
    candidate: {
      candidate_id: candidate.id || candidate.candidate_id,
      candidate_name: candidate.name || candidate.candidate_name || "",
      candidate_email: candidate.email || candidate.candidate_email || "",
      candidate_phone: candidate.phone || candidate.candidate_phone || "",
      candidate_linkedin_url: candidate.linkedin_url || candidate.candidate_linkedin_url || "",
      candidate_avatar: candidate.avatar || candidate.candidate_avatar || "",
      candidate_avatar_url: candidate.avatar_url || candidate.candidate_avatar_url || "",
      avatar_url: candidate.avatar_url || candidate.candidate_avatar_url || "",
      avatar_full_url: candidate.avatar_full_url || candidate.candidate_avatar_url || candidate.avatar_url || "",
      verified: candidate.verified || false,
    }
  };
}

// Keep update endpoints for future use; degrade gracefully if 404
export async function updateCandidateAccount(payload) {
  try {
    return await apiFetch(`${CareersAPIBase}/candidate/account`, {
      method: "PUT",
      body: payload,
    });
  } catch (e) {
    if (e && e.status === 404) {
      throw new Error("Account update is not available on this deployment.");
    }
    throw e;
  }
}

export async function changeCandidatePassword(payload) {
  return apiFetch(`${CareersAPIBase}/candidate/password`, {
    method: "PUT",
    body: payload,
  });
}

export async function uploadCandidateAvatar(file) {
  const form = new FormData();
  form.append("avatar", file);
  return apiFetch(`${CareersAPIBase}/candidate/account/avatar`, {
    method: "POST",
    body: form,
    headers: {}, // let the browser set multipart/form-data
  });
}

//////////////////////////////
// Applications / resume upload
//////////////////////////////
export async function submitApplication({ id, jobId, file, ...payload }) {
  const jid = jobId ?? id;
  if (!jid) throw new Error("jobId is required");

  let body = payload || {};

  if (file instanceof File || (file && typeof file === "object" && "name" in file)) {
    const fd = new FormData();
    fd.append("resume", file);
    Object.entries(body).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });
    body = fd;
  }

  return apiFetch(`${CareersAPIBase}/jobs/${encodeURIComponent(jid)}/apply`, {
    method: "POST",
    body,
  });
}

export function getResumeDownloadUrl({ jobReqId, candidateId }) {
  return `${CareersAPIBase}/resumes/${encodeURIComponent(jobReqId)}/${encodeURIComponent(
    candidateId
  )}`;
}

//////////////////////////////
// Public, read-only helpers (org REQUIRED)
//////////////////////////////
export async function getJobPublic({ orgId, id }) {
  const finalOrg = getOrgIdOrThrow(orgId);

  const qs = new URLSearchParams();
  qs.set("orgId", String(finalOrg));

  const url = `${PublicAPIBase}/job/${encodeURIComponent(id)}?${qs.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = await res.text();
    } catch {}
    throw new Error(
      `Public job fetch failed (${res.status}): ${bodyText || res.statusText}`
    );
  }

  return res.json();
}

export async function getJobsPublic({ orgId, q = "", page = 1, pageSize = 20 }) {
  const finalOrg = getOrgIdOrThrow(orgId);

  const qs = new URLSearchParams();
  qs.set("orgId", String(finalOrg));
  if (q) qs.set("q", q);
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));

  const url = `${PublicAPIBase}/jobs?${qs.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    let body = "";
    try {
      body = await res.text();
    } catch {}
    throw new Error(
      `Public jobs fetch failed (${res.status}): ${body || res.statusText}`
    );
  }

  return res.json();
}

export async function applyToJob({ orgId, id, file, preferredInterviewUtc }) {
  const token = getCandidateToken();
  if (!token) throw new Error("Please sign in first.");

  const u = new URL(`${API_ROOT}/careers/jobs/${encodeURIComponent(id)}/apply`, window.location.origin);

  if (orgId) u.searchParams.set("orgId", String(orgId));

  const fd = new FormData();
  if (file) fd.append("resume", file);
  if (preferredInterviewUtc) fd.append("preferredInterviewUtc", preferredInterviewUtc);

  const r = await fetch(u, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
    credentials: "include",
  });

  if (r.status === 401 || r.status === 403) throw new Error("Unauthorized");
  if (!r.ok) throw new Error(`Apply failed (${r.status})`);
  return r.json();
}

//////////////////////////////
// Backward-compatibility aliases
//////////////////////////////
export async function getJobs(params = {}) {
  return searchJobs(params);
}
export const candResetPassword = candPasswordResetSubmit;

export function logout() {
  setCandidateToken("");
}
